import { useAtom } from '@reatom/npm-react'
import { useTranslation } from 'react-i18next'
import styled, { css } from 'styled-components'

import { Typography } from '@blade/ui-kit'
import { Numeric } from '@blade/ui-kit'
import { nativeNetworkCurrencyAtom } from '@root/entities/currency'
import { Trend } from '@root/entities/trend'
import { Share2Icon } from '@root/shared/ui-kit'

import BEST_DEAL_GRAPHIC from './best-deal-graphic.svg'
import { BestDetailEntry } from './best-detail-entry'

import type { BestDeal } from '@root/entities/profile'
import type { ClassNameProps } from '@root/shared/react'

type DealPositionDetailsBackground = 'cells' | 'no'
type DealPositionDetailsProps = {
  bestDeal: BestDeal.BestDeal
  onShare: () => void
  background?: DealPositionDetailsBackground
} & ClassNameProps

export const BestDealView: React.FC<DealPositionDetailsProps> = ({
  bestDeal,
  className,
  onShare,
  background = 'no',
}) => {
  const { t } = useTranslation()

  const [nativeCurrency] = useAtom(nativeNetworkCurrencyAtom)

  return (
    <Root $background={background} className={className}>
      <Content>
        <PnlContainer>
          <PnlContainerTitle>{t('profile.best_deal_title')}</PnlContainerTitle>
          <PnlPercents data-trend={Trend.of(bestDeal.pnlPercents)}>
            <Numeric value={bestDeal.pnlPercents} preset="percents" />%
            <PnlPercentsTag>{t('common.pnl')}</PnlPercentsTag>
          </PnlPercents>
          <PnlNative>
            <Numeric
              value={bestDeal.pnlNative}
              preset="currency-amount-short"
            />
            <PnlNativeCurrency>{nativeCurrency.ticker}</PnlNativeCurrency>
          </PnlNative>
          <PnlUsd>
            <Numeric
              value={bestDeal.pnlUsd}
              preset="currency-amount-short"
              currency="$"
            />
          </PnlUsd>
        </PnlContainer>
        <BestDetailEntryStyled bestDeal={bestDeal} />
        <ShareButton type="button" onClick={onShare}>
          <ShareButtonText>{t('common.share')}</ShareButtonText>
          <Share2Icon width={16} height={16} color={'var(--text-1)'} />
        </ShareButton>
      </Content>
    </Root>
  )
}

const cellsBackground = css`
  background:
    url('${BEST_DEAL_GRAPHIC}'),
    repeating-linear-gradient(
      0deg,
      var(--backgroundA-2),
      var(--backgroundA-2) 1px,
      transparent 1px,
      transparent 10px
    ),
    repeating-linear-gradient(
      90deg,
      var(--backgroundA-2),
      var(--backgroundA-2) 1px,
      transparent 1px,
      transparent 10px
    );
  background-color: var(--background-1);
`
const noBackground = css`
  background: url('${BEST_DEAL_GRAPHIC}'), var(--appearanceA-4);
`

const Root = styled.div<{ $background: DealPositionDetailsBackground }>`
  ${(x) => (x.$background === 'cells' ? cellsBackground : noBackground)}

  background-position: 0 center;
  background-repeat: no-repeat;
  background-size: contain;
  border-radius: 12px;
  padding: 10px;
`

const Content = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  grid-template-rows: auto 1fr auto;
  height: 100%;
  gap: 0 15px;
`

const PnlContainer = styled.div`
  display: grid;
  grid-template-rows: repeat(4, auto);
  padding-top: 7px;
  grid-column: 1;
  grid-row: 1;
`

const PnlContainerTitle = styled(Typography)`
  color: var(--text-9);
  font-size: 12px;
  font-weight: 600;
  line-height: 16px;
  margin-bottom: 5px;
`

const PnlPercents = styled.div`
  font-weight: 700;
  font-size: 20px;
  line-height: 115%;
  &[data-trend='up'] {
    color: var(--up-10);
  }
  &[data-trend='neutral'] {
    color: var(--text-11);
  }
  &[data-trend='down'] {
    color: var(--down-10);
  }

  margin-bottom: 5px;
`

const PnlPercentsTag = styled.span`
  font-size: 12px;
  font-style: normal;
  font-weight: 600;
  line-height: 16px;
  margin-left: 2px;
`

const PnlNative = styled.div`
  color: var(--textA-12);
  font-size: 16px;
  font-weight: 700;
  line-height: 20px;
`

const PnlNativeCurrency = styled.span`
  font-size: 12px;
  font-weight: 600;
  line-height: 16px;
  margin-left: 5px;
`

const PnlUsd = styled.div`
  color: var(--textA-9);
  font-size: 13px;
  font-weight: 600;
  line-height: 16px;
`

const ShareButton = styled.button`
  all: unset;
  display: inline-flex;
  height: 24px;
  width: fit-content;
  cursor: pointer;
  padding: 0px 8px;
  justify-content: center;
  align-items: center;
  gap: 4px;
  flex-shrink: 0;
  border-radius: 6px;
  background: var(--backgroundA-11);
  grid-column: 1;
  grid-row: 3;
`

const ShareButtonText = styled.div`
  color: var(--text-1);
  font-size: 12px;
  font-weight: 700;
  line-height: 100%;
`

const BestDetailEntryStyled = styled(BestDetailEntry)`
  grid-column: 2;
  grid-row: 1 / span 3;
`
